module Widgets.Form.List where

import Common

import Concur.Core (Widget)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Argonaut (Json, decodeJson, encodeJson)
import Data.Argonaut as J
import Data.Array as Arr
import Data.FunctorWithIndex (mapWithIndex)
import Error (wrapJsonError')
import Internal.JsonUtil (assertObject)
import Widgets.Form.Access (FieldAccessLevel(..))
import Widgets.Form.Class (class FormWidget, FormContext, FormWidgetResult, addParentId, fieldAccessLevel, fieldCanUpdate, fieldHasVisibleDescendant, fieldClass, fieldId, formWidgetState, getData, getId, isLeaf, fieldPaths, setData, toWidget)
import Widgets.Form.Dynamic (DynFormWidget)


newtype ListWidget = ListWidget ListWidgetType
instance showLW :: Show ListWidget where
      show (ListWidget {id, label, entries}) = show {id, label, entries}

type ListWidgetType = ListWidgetInit
    ( entries :: Array DynFormWidget
    , addBtns :: Array ListWidgetButton
    -- Function for loading from JSON into this list widget.
    , load :: ListWidgetLoadFunc
    , loadExist :: ListWidgetLoadExistingFunc
    )

-- An add button on a list widget.
--
-- These will create buttons which will run the `newEntries` widget action when
-- clicked. `newEntries` may do whatever necessary to generate a list of
-- widgets to add to the list. For example, in the case of the expectations
-- chooser, this will show a modal and then return a list of widgets for the
-- selected expectations.
type ListWidgetButton =
    { label :: String
    , newEntries :: FormContext -> Widget HTML (Array DynFormWidget)
    }

-- Function for loading entries from their JSON data.
type ListWidgetLoadFunc = FormContext -> Json -> Either ErrorWrapper DynFormWidget

-- Function for loading entries from their JSON data.
type ListWidgetLoadExistingFunc = FormContext -> DynFormWidget -> Json -> Either ErrorWrapper DynFormWidget

-- Constructor arguments
type ListWidgetInit r =
    { id :: String
    , label :: String
    , cls :: String
    | r
    }

-- | Constructs a simple list widget, where new entries are added by cloning
-- | the given `template` widget.
simpleListWidget :: ListWidgetInit (template :: DynFormWidget) -> ListWidget
simpleListWidget { id, label, template, cls } = ListWidget
    { id, label
    , cls
    , entries: []
    -- Create new entries by just returning the template when "add" is clicked.
    , addBtns: [{label: lookupTranslation "add", newEntries: \_ -> pure [template]}]
    -- Load entries from json by calling `setData` on the template.
    , load: \fc json -> setData fc template json
    , loadExist: \fc oldw json -> setData fc oldw json
    }


-- Constructor arguments for `modalListWidget`.
type ModalListWidgetInit r = ListWidgetInit
    ( addBtns :: Array ListWidgetButton
    , load :: ListWidgetLoadFunc
    , loadExist :: ListWidgetLoadExistingFunc
    | r )

-- | Constructs a modal list widget, which will run a widget action specified
-- | by `new` to show a modal, and then add the return from that modal to the
-- | list.
-- |
-- | `load` should also be specified to load data into the list.
modalListWidget :: ModalListWidgetInit () -> ListWidget
modalListWidget { id, label, addBtns, load, loadExist, cls } =
    ListWidget { id, label, addBtns, load, loadExist, cls, entries: [] }


instance listFW :: FormWidget ListWidget where
    getId (ListWidget { id }) = id
    isLeaf _ = false
    asField _ = Nothing -- TODO?
    fieldPaths fc (ListWidget state) =
        [ fieldId fc state.id ] <>
            Arr.concat (mapWithIndex entryPaths state.entries)
      where
        entryPaths idx widget = fieldPaths subfc widget
          where
            subfc = addParentId (fc { curIndex = idx }) $ show idx

    toWidget fc (ListWidget state) = (ListWidget <$> _) <$> html
      where
        html :: Widget HTML (FormWidgetResult ListWidgetType)
        html = D.div
            [ P.className $ fieldClass state
            , P.key id
            ]
            [ D.div [P.className "header"]
                [ D.h4' [D.text state.label] ]
            , D.div [P.className "list w-full"] subWidgets
            , formWidgetState <$> addButtons
            ]
        id = fieldId fc state.id
        canModify = fieldCanUpdate fc state.id

        addButtons :: Widget HTML ListWidgetType
        addButtons | canModify = D.div [P.className "list-add-buttons btn-group"] $
            map addButton state.addBtns
        addButtons = D.div [P.className "list-add-buttons btn-group"] []

        addButton :: ListWidgetButton -> Widget HTML ListWidgetType
        addButton { label, newEntries } = do
            _ <- D.button
               [ P.className "btn list-add widget-input"
               , P.onClick
               , P._type "button" ]
               [ D.text label ]
            ents <- newEntries fc
            pure $ state { entries = Arr.concat [state.entries, ents] }

        subWidgets :: Array (Widget HTML (FormWidgetResult ListWidgetType))
        subWidgets = mapWithIndex subWidget state.entries

        subWidget :: Int -> DynFormWidget -> Widget HTML (FormWidgetResult ListWidgetType)
        subWidget idx w = case fieldAccessLevel subfc (getId w) of
            AccessNone | isLeaf w || not (fieldHasVisibleDescendant subfc (getId w)) ->
                formWidgetState state <$ D.text ""
            _ -> D.div
                [ P.className "list-entry" ]
                ( [ renderEntry ]
                <> if canModify
                    then [ D.button
                        [ (formWidgetState $ removeEntry idx) <$ P.onClick
                        , P.className "btn"
                        , P._type "button" ]
                        [ D.text $ lookupTranslation "delete" ]
                        ]
                    else []
                )
          where
            subfc = addParentId (fc { curIndex = idx }) $ show idx

            renderEntry = (updateEntry idx <$> _) <$> toWidget subfc w

        updateEntry :: Int -> DynFormWidget -> ListWidgetType
        updateEntry idx w = state { entries = _ } <$>
            fromMaybe state.entries $ Arr.updateAt idx w state.entries

        removeEntry :: Int -> ListWidgetType
        removeEntry idx = state { entries = _ } <$>
            fromMaybe state.entries $ Arr.deleteAt idx state.entries

    getData fc (ListWidget state) =
        encodeJson $ map (getData fc) state.entries
    setData fc (ListWidget state) json = do
        entryData :: Array Json <- wrapJsonError' json $ decodeJson json
        let initialEntries =
                -- Autofill data is patch-like, not authoritative data forming
                -- a complete document.
                -- Empty list data from autofill means there are no incoming
                -- rows to apply, not that the user's existing rows should
                -- be cleared.
                if fc.isAutofilling
                then state.entries

                -- Non-autofill document data is authoritative. If incoming list
                -- rows do not have explicit indexes, replace the current list
                -- state so saved rows are not appended to existing rows.
                else if any hasExplicitIndex entryData
                    then state.entries
                    else []

        entries <- foldM loadEntry initialEntries entryData
        Right $ ListWidget $ state { entries = entries }
      where
        entryIndex ejson = fromMaybe (-1) <$> wrapJsonError' ejson
            (assertObject ejson >>= (_ .:? "_index"))

        hasExplicitIndex ejson | J.isArray ejson = false
        hasExplicitIndex ejson = case entryIndex ejson of
            Right idx -> idx >= 0
            Left _ -> false

        -- This is a hack to work around the fact that PHP doesn't properly
        -- distinguish between empty arrays and empty objects, even when
        -- serializing to JSON, which does make that distinction.
        loadEntry ents ejson | ejson == J.fromArray [] =
            loadEntry ents J.jsonEmptyObject

        loadEntry ents ejson = do
            idx :: Int <- entryIndex ejson
            case Arr.index ents idx of
                Nothing -> do
                    entry <- state.load fc ejson
                    pure $ ents <> [entry]
                Just oldent -> do
                    entry <- state.loadExist fc oldent ejson
                    case Arr.updateAt idx entry ents of
                        Nothing -> pure $ ents <> [entry]
                        Just ents' -> pure ents' -- This shouldn't happen
